import {
  HELPER_API_BASE_PATH,
  HELPER_DEFAULT_PORT,
} from '@skyrover/shared/constants';

const HELPER_BASE_URL = `http://127.0.0.1:${HELPER_DEFAULT_PORT}${HELPER_API_BASE_PATH}`;
const REQUEST_TIMEOUT_MS = 2500;

export interface HelperHealth {
  status: 'ok' | 'setup_required' | 'error';
  version: string;
  token: string;
  setupComplete: boolean;
  protocolVersion: string;
}

export interface HelperPortInfo {
  path: string;
  label?: string;
}

interface HelperPortsResponse {
  ports?: HelperPortInfo[];
}

export interface UploadSketchParams {
  code: string;
  fqbn: string;
  port: string;
}

export interface HelperUploadResult {
  log: string;
}

interface HelperUploadResponse {
  success?: boolean;
  log?: string;
  error?: string;
  jobId?: string;
}

const UPLOAD_TIMEOUT_MS = 180_000;
const SETUP_TIMEOUT_MS = 15 * 60 * 1000;
const CORE_STATUS_TIMEOUT_MS = 120_000;

export type HelperConnectionState = 'ready' | 'setup_required' | 'offline';

export function resolveHelperConnectionState(
  health: HelperHealth | null,
): HelperConnectionState {
  if (!health?.token) {
    return 'offline';
  }
  if (health.status === 'ok' && health.setupComplete) {
    return 'ready';
  }
  if (health.status === 'setup_required') {
    return 'setup_required';
  }
  return 'offline';
}

async function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...init,
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
}

export async function fetchHelperHealth(): Promise<HelperHealth | null> {
  try {
    const response = await fetchWithTimeout(`${HELPER_BASE_URL}/health`);
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as HelperHealth;
  } catch {
    return null;
  }
}

export async function fetchHelperPorts(token: string): Promise<HelperPortInfo[]> {
  const response = await fetchWithTimeout(`${HELPER_BASE_URL}/ports`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to list COM ports from helper.');
  }

  const data = (await response.json()) as HelperPortsResponse;
  return data.ports ?? [];
}

export async function setupArduinoViaHelper(): Promise<{
  success: boolean;
  log: string;
  error?: string;
}> {
  const health = await fetchHelperHealth();
  if (!health?.token) {
    throw new Error('Skyrover Helper is not running.');
  }

  const response = await fetch(`${HELPER_BASE_URL}/setup/arduino`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${health.token}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(SETUP_TIMEOUT_MS),
  });

  return parseSetupResponse(response);
}

export interface BoardCoreStatus {
  id: string;
  description: string;
  installed: boolean;
  version?: string;
}

export interface BoardCoreStatusResponse {
  cliAvailable: boolean;
  cores: BoardCoreStatus[];
}

export async function fetchBoardCoreStatus(): Promise<BoardCoreStatusResponse> {
  const health = await fetchHelperHealth();
  if (!health?.token) {
    throw new Error('Skyrover Helper is not running.');
  }

  const response = await fetch(`${HELPER_BASE_URL}/setup/cores`, {
    headers: {
      Authorization: `Bearer ${health.token}`,
    },
    signal: AbortSignal.timeout(CORE_STATUS_TIMEOUT_MS),
  });

  if (response.status === 404) {
    throw new Error(
      `Board Manager needs Skyrover Helper v0.1.4 or newer (this PC has v${health.version}). Download and install the latest helper, then reopen Board Manager.`,
    );
  }

  if (!response.ok) {
    let detail = 'Failed to read board package status.';
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) {
        detail = body.error;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(detail);
  }

  return (await response.json()) as BoardCoreStatusResponse;
}

export interface BoardInstallProgressEvent {
  type: 'progress' | 'log' | 'done';
  stage?: string;
  percent?: number;
  message?: string;
  log?: string;
  success?: boolean;
  error?: string;
}

export async function installBoardCoresViaHelper(
  cores: string[],
  onEvent?: (event: BoardInstallProgressEvent) => void,
): Promise<{
  success: boolean;
  log: string;
  error?: string;
}> {
  const health = await fetchHelperHealth();
  if (!health?.token) {
    throw new Error('Skyrover Helper is not running.');
  }

  const response = await fetch(`${HELPER_BASE_URL}/setup/cores?stream=1`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${health.token}`,
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
    },
    body: JSON.stringify({ cores }),
    signal: AbortSignal.timeout(SETUP_TIMEOUT_MS),
  });

  if (response.status === 409) {
    return {
      success: false,
      log: '',
      error: 'Board setup is already running.',
    };
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok && contentType.includes('application/json')) {
    return parseSetupResponse(response);
  }

  if (!response.ok) {
    throw new Error('Board package install failed.');
  }

  if (!contentType.includes('application/x-ndjson') || !response.body) {
    return parseSetupResponse(response);
  }

  return readInstallProgressStream(response.body, onEvent);
}

async function readInstallProgressStream(
  body: ReadableStream<Uint8Array>,
  onEvent?: (event: BoardInstallProgressEvent) => void,
): Promise<{ success: boolean; log: string; error?: string }> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalLog = '';
  let finalSuccess = false;
  let finalError: string | undefined;
  let accumulatedLog = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }

      const event = JSON.parse(trimmed) as BoardInstallProgressEvent;
      onEvent?.(event);

      if (event.type === 'log' && event.log) {
        accumulatedLog += event.log;
      }

      if (event.type === 'done') {
        finalLog = event.log ?? accumulatedLog;
        finalSuccess = event.success === true;
        finalError = event.error;
      }
    }
  }

  if (buffer.trim()) {
    const event = JSON.parse(buffer.trim()) as BoardInstallProgressEvent;
    onEvent?.(event);
    if (event.type === 'log' && event.log) {
      accumulatedLog += event.log;
    }
    if (event.type === 'done') {
      finalLog = event.log ?? accumulatedLog;
      finalSuccess = event.success === true;
      finalError = event.error;
    }
  }

  const result: { success: boolean; log: string; error?: string } = {
    success: finalSuccess,
    log: finalLog || accumulatedLog,
  };
  if (finalError !== undefined) {
    result.error = finalError;
  }
  return result;
}

async function parseSetupResponse(response: Response): Promise<{
  success: boolean;
  log: string;
  error?: string;
}> {
  const data = (await response.json()) as {
    success?: boolean;
    log?: string;
    error?: string;
  };

  if (response.status === 409) {
    return {
      success: false,
      log: data.log ?? '',
      error: data.error ?? 'Setup is already running.',
    };
  }

  if (!response.ok) {
    throw new Error(data.error ?? 'Setup request failed.');
  }

  const result: { success: boolean; log: string; error?: string } = {
    success: data.success === true,
    log: data.log ?? '',
  };
  if (data.error !== undefined) {
    result.error = data.error;
  }
  return result;
}

export async function fetchMissingLibraries(libraries: string[]): Promise<string[]> {
  if (libraries.length === 0) {
    return [];
  }

  const health = await fetchHelperHealth();
  if (!health?.token) {
    throw new Error('Skyrover Helper is not running.');
  }

  const response = await fetch(`${HELPER_BASE_URL}/libraries/check`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${health.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ libraries }),
    signal: AbortSignal.timeout(CORE_STATUS_TIMEOUT_MS),
  });

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    let detail = 'Failed to check installed libraries.';
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) {
        detail = body.error;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(detail);
  }

  const data = (await response.json()) as { missing?: string[] };
  return data.missing ?? [];
}

export async function uploadViaHelper(
  token: string,
  params: UploadSketchParams,
): Promise<HelperUploadResult> {
  const response = await fetch(`${HELPER_BASE_URL}/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fqbn: params.fqbn,
      port: params.port,
      sketch: {
        mainFile: 'sketch.ino',
        files: {
          'sketch.ino': params.code,
        },
      },
    }),
    signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
  });

  const data = (await response.json()) as HelperUploadResponse;

  if (!response.ok || data.success === false) {
    throw new Error(data.error ?? 'Helper upload failed.');
  }

  return {
    log: data.log ?? 'Upload complete.',
  };
}
